import API from "../../../api/index";

import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { useNavigate } from "react-router-dom";

import { boxStyle, logColor } from "../../../theme";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const SOURCE_CONFIG = {
  CriticalEvents: {
    label: "Critical Events",
    color: logColor[1] || "#931A1D",
    dateField: "timestamp",
  },

  TiresiaAnomalies: {
    label: "Tiresia Anomalies",
    color: "#7C4DFF",
    dateField: "last_seen",
  },

  Aggregations: {
    label: "Aggregations",
    color: "#E67E22",
    dateField: "last_seen",
  },

  ThreatIntelligence: {
    label: "Threat Intelligence",
    color: logColor[4] || "#C485BF",
    dateField: "timestamp",
  },
};

const EMPTY_HIGHLIGHTS = {
  CriticalEvents: [],
  TiresiaAnomalies: [],
  Aggregations: [],
  ThreatIntelligence: [],
};

const Highlights = () => {
  const navigate = useNavigate();

  const [charged, setCharged] = useState(true);
  const [rangeHours, setRangeHours] = useState(24);

  const [highlights, setHighlights] = useState(EMPTY_HIGHLIGHTS);

  /*
   * Manteniamo esattamente le date utilizzate per effettuare
   * la richiesta al backend.
   *
   * Le stesse date vengono riutilizzate nelle navigazioni.
   */
  const [currentDateRange, setCurrentDateRange] = useState({
    dateFrom: null,
    dateTo: null,
  });

  const userTimezone = useMemo(() => dayjs.tz.guess(), []);

  /*
   * =========================================================
   * RECUPERO DATI
   * =========================================================
   */
  useEffect(() => {
    let mounted = true;

    const loadHighlights = async () => {
      try {
        setCharged(true);

        const dateTo = dayjs().utc();
        const dateFrom = dateTo.subtract(rangeHours, "hour");

        const dateFromISO = dateFrom.toISOString();
        const dateToISO = dateTo.toISOString();

        if (mounted) {
          setCurrentDateRange({
            dateFrom: dateFromISO,
            dateTo: dateToISO,
          });
        }

        const result = await API.getHighlightsHome({
          dateFrom: dateFromISO,
          dateTo: dateToISO,
        });

        if (!mounted) {
          return;
        }

        setHighlights({
          CriticalEvents: Array.isArray(
            result.data?.CriticalEvents
          )
            ? result.data.CriticalEvents
            : [],

          TiresiaAnomalies: Array.isArray(
            result.data?.TiresiaAnomalies
          )
            ? result.data.TiresiaAnomalies
            : [],

          Aggregations: Array.isArray(
            result.data?.Aggregations
          )
            ? result.data.Aggregations
            : [],

          ThreatIntelligence: Array.isArray(
            result.data?.ThreatIntelligence
          )
            ? result.data.ThreatIntelligence
            : [],
        });
      } catch (error) {
        if (!mounted) {
          return;
        }

        console.error(
          "[Highlights] Errore durante il recupero dei dati:",
          error
        );

        setHighlights({
          CriticalEvents: [],
          TiresiaAnomalies: [],
          Aggregations: [],
          ThreatIntelligence: [],
        });

        setCurrentDateRange({
          dateFrom: null,
          dateTo: null,
        });
      } finally {
        if (mounted) {
          setCharged(false);
        }
      }
    };

    loadHighlights();

    return () => {
      mounted = false;
    };
  }, [rangeHours]);

  /*
   * =========================================================
   * DATI TABELLA
   * =========================================================
   *
   * Viene generata una riga per ogni sorgente:
   *
   * - CriticalEvents
   * - TiresiaAnomalies
   * - Aggregations
   * - ThreatIntelligence
   */
  const tableData = useMemo(() => {
    const createGroup = (source) => {
      const config = SOURCE_CONFIG[source];

      const items = Array.isArray(highlights[source])
        ? highlights[source]
        : [];

      const validItems = [];
      const timestamps = [];

      items.forEach((item) => {
        const rawDate = item?.[config.dateField];

        if (!rawDate) {
          return;
        }

        const parsedDate = dayjs(rawDate);

        if (!parsedDate.isValid()) {
          return;
        }

        timestamps.push(parsedDate.valueOf());
        validItems.push(item);
      });

      if (timestamps.length === 0) {
        return {
          source,
          firstTimestamp: null,
          lastTimestamp: null,
          count: 0,
          items: [],
        };
      }

      return {
        source,
        firstTimestamp: Math.min(...timestamps),
        lastTimestamp: Math.max(...timestamps),
        count: validItems.length,
        items: validItems,
      };
    };

    return [
      createGroup("CriticalEvents"),
      createGroup("TiresiaAnomalies"),
      createGroup("Aggregations"),
      createGroup("ThreatIntelligence"),
    ];
  }, [highlights]);

  /*
   * =========================================================
   * CAMBIO RANGE
   * =========================================================
   */
  const handleRangeChange = (_, newRange) => {
    if (newRange === null) {
      return;
    }

    setRangeHours(newRange);
  };

  /*
   * =========================================================
   * PARAMETRI TIRESIA
   * =========================================================
   */
  const getTiresiaNavigationParams = () => {
    const anomalies = Array.isArray(
      highlights.TiresiaAnomalies
    )
      ? highlights.TiresiaAnomalies
      : [];

    /*
     * Recuperiamo tutti gli anomaly_types presenti in tutti
     * gli elementi restituiti dall'API.
     *
     * La gestione supporta sia anomaly_types come array,
     * sia anomaly_types come singola stringa.
     */
    const uniqueAnomalyTypes = [
      ...new Set(
        anomalies.flatMap((anomaly) => {
          const anomalyTypes = anomaly?.anomaly_types;

          if (Array.isArray(anomalyTypes)) {
            return anomalyTypes;
          }

          if (
            anomalyTypes !== undefined &&
            anomalyTypes !== null &&
            anomalyTypes !== ""
          ) {
            return [anomalyTypes];
          }

          return [];
        })
      ),
    ].filter(
      (anomalyType) =>
        anomalyType !== undefined &&
        anomalyType !== null &&
        anomalyType !== ""
    );

    /*
     * Recuperiamo tutti i peak_score numerici
     * e calcoliamo il valore minimo.
     */
    const peakScores = anomalies
      .map((anomaly) => Number(anomaly?.peak_score))
      .filter((peakScore) => Number.isFinite(peakScore));

    const minimumPeakScore =
      peakScores.length > 0 ? Math.min(...peakScores) : null;

    return {
      uniqueAnomalyTypes,
      minimumPeakScore,
    };
  };

  /*
   * =========================================================
   * PARAMETRI DATE
   * =========================================================
   */
  const getDateRangeParams = () => {
    const { dateFrom, dateTo } = currentDateRange;

    if (!dateFrom || !dateTo) {
      return null;
    }

    return new URLSearchParams({
      dateFrom,
      dateTo,
    });
  };

  /*
   * =========================================================
   * CLICK RIGA -> NAVIGATE
   * =========================================================
   */
  const handleRowClick = (row) => {
    /*
     * Se non ci sono elementi, non eseguiamo la navigazione.
     */
    if (row.count === 0) {
      return;
    }

    /*
     * =======================================================
     * CRITICAL EVENTS
     * =======================================================
     */
    if (row.source === "CriticalEvents") {
      const params = getDateRangeParams();

      if (!params) {
        return;
      }

      navigate(`../security/critical?${params.toString()}`);

      return;
    }

    /*
     * =======================================================
     * TIRESIA ANOMALIES
     * =======================================================
     *
     * Parametri:
     *
     * - hours: filtro temporale selezionato
     * - anomaly_types: valori univoci restituiti dall'API
     * - peak_score: peak_score minimo restituito dall'API
     */
    if (row.source === "TiresiaAnomalies") {
      const { uniqueAnomalyTypes, minimumPeakScore } =
        getTiresiaNavigationParams();

      const params = new URLSearchParams({
        hours: String(rangeHours),
      });

      if (uniqueAnomalyTypes.length > 0) {
        params.set(
          "anomaly_types",
          uniqueAnomalyTypes.join(",")
        );
      }

      if (minimumPeakScore !== null) {
        params.set(
          "peak_score",
          String(minimumPeakScore)
        );
      }

      navigate(
        `../tiresia-engine/tiresia-analysis?${params.toString()}`
      );

      return;
    }

    /*
     * =======================================================
     * AGGREGATIONS
     * =======================================================
     */
    if (row.source === "Aggregations") {
      const params = getDateRangeParams();

      if (!params) {
        return;
      }

      navigate(`../aggregations?severity=high&${params.toString()}`);

      return;
    }

    /*
     * =======================================================
     * THREAT INTELLIGENCE
     * =======================================================
     */
    if (row.source === "ThreatIntelligence") {
      const params = getDateRangeParams();

      if (!params) {
        return;
      }

      navigate(
        `../security/threat-intelligence?${params.toString()}`
      );
    }
  };

  /*
   * =========================================================
   * FORMAT DATE
   * =========================================================
   */
  const formatDate = (date) => {
    if (date === null || date === undefined) {
      return "-";
    }

    const parsedDate = dayjs(date);

    if (!parsedDate.isValid()) {
      return "-";
    }

    return parsedDate
      .tz(userTimezone)
      .format("DD/MM/YYYY HH:mm:ss");
  };

  /*
   * =========================================================
   * SOURCE CONFIG
   * =========================================================
   */
  const getSourceConfig = (source) => {
    return (
      SOURCE_CONFIG[source] || {
        label: source || "-",
        color: "#777",
      }
    );
  };

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */
  return (
    <Box
      sx={{
        ...boxStyle,
        p: 2,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* =====================================================
          HEADER
         ===================================================== */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#888",
            }}
          >
            Highlights
          </Typography>

          <Typography
            sx={{
              fontSize: 11,
              color: "#555",
              mt: 0.3,
            }}
          >
            Relevant security activity in the last{" "}
            {rangeHours} hours
          </Typography>
        </Box>

        {/* RANGE BUTTONS */}
        <ToggleButtonGroup
          value={rangeHours}
          exclusive
          onChange={handleRangeChange}
          size="small"
          disabled={charged}
          sx={{
            "& .MuiToggleButton-root": {
              px: 1.5,
              py: 0.5,
              fontSize: 11,
              textTransform: "none",
            },
          }}
        >
          <ToggleButton value={24}>Last 24h</ToggleButton>
          <ToggleButton value={48}>Last 48h</ToggleButton>
          <ToggleButton value={72}>Last 72h</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Divider
        sx={{
          my: 1.5,
          opacity: 0.3,
        }}
      />

      {/* =====================================================
          CONTENUTO
         ===================================================== */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {charged ? (
          /*
           * =================================================
           * LOADING
           * =================================================
           */
          <Stack
            spacing={1}
            sx={{
              flex: 1,
            }}
          >
            {[...Array(4)].map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={50}
                sx={{
                  bgcolor: "rgba(255,255,255,0.05)",
                }}
              />
            ))}
          </Stack>
        ) : (
          /*
           * =================================================
           * TABLE
           * =================================================
           */
          <TableContainer
            sx={{
              flex: 1,
              width: "100%",
            }}
          >
            <Table
              size="small"
              sx={{
                width: "100%",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    First Timestamp
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Last Timestamp
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Source
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      width: 90,
                    }}
                  >
                    Count
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData.map((row) => {
                  const sourceConfig = getSourceConfig(
                    row.source
                  );

                  return (
                    <TableRow
                      key={row.source}
                      hover={row.count > 0}
                      onClick={() => handleRowClick(row)}
                      sx={{
                        cursor:
                          row.count > 0
                            ? "pointer"
                            : "default",

                        height: 58,

                        ...(row.count > 0 && {
                          "&:hover": {
                            backgroundColor:
                              "rgba(255,255,255,0.03)",
                          },
                        }),
                      }}
                    >
                      {/* FIRST TIMESTAMP */}
                      <TableCell
                        sx={{
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          py: 1.5,
                        }}
                      >
                        {formatDate(row.firstTimestamp)}
                      </TableCell>

                      {/* LAST TIMESTAMP */}
                      <TableCell
                        sx={{
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          py: 1.5,
                        }}
                      >
                        {formatDate(row.lastTimestamp)}
                      </TableCell>

                      {/* SOURCE */}
                      <TableCell
                        sx={{
                          py: 1.5,
                        }}
                      >
                        <Chip
                          label={sourceConfig.label}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: sourceConfig.color,
                            borderColor: sourceConfig.color,
                            fontSize: 11,
                            fontWeight: 500,
                            height: 24,
                          }}
                        />
                      </TableCell>

                      {/* COUNT */}
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          py: 1.5,
                        }}
                      >
                        {row.count}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* =====================================================
          DESCRIZIONE
         ===================================================== */}
      <Typography
        sx={{
          fontSize: 12,
          color: "#555",
          mt: "auto",
          pt: 2,
        }}
      >
        Events are grouped by source. First Timestamp and Last
        Timestamp indicate the first and last event detected
        during the selected time range. Click a row to open the
        related analysis.
      </Typography>
    </Box>
  );
};

export default Highlights;